/** Finished AI photos are separate from untouched uploads and from option IDs. */
export interface AiPhotoContext {
  optionId: string; assetId: string; originalUrl: string; key: string;
  templateId: string; target: string; texts: Record<string, string>;
}
export type AiPhotoState =
  | { phase: 'working' }
  | { phase: 'ready'; url: string }
  | { phase: 'missing'; message: string };
export interface AiPhotoResult { url: string | null; reason?: string; notice?: string }

/** Pixel rounding at 300 dpi must not make the same physical aspect a new design. */
export function aiPhotoKey(assetId: string, spec: string, width: number, height: number, dpi: number): string {
  const mm = (px: number): number => Math.round(px / dpi * 254) / 10;
  return JSON.stringify([assetId, spec, mm(width) / mm(height)]);
}

export class AiPhotoDesigns {
  private readonly active = new Map<string, AiPhotoContext>();
  private readonly ready = new Map<string, string>();
  private readonly generating = new Map<string, Promise<AiPhotoResult>>();
  constructor(private readonly ports: {
    request(context: AiPhotoContext, cacheOnly: boolean): Promise<AiPhotoResult>;
    changed(context: AiPhotoContext, state: AiPhotoState, cacheOnly: boolean): void;
  }) {}

  /** A different form invalidates callbacks, while semantically matching results survive. */
  reset(): void { this.active.clear(); }

  forget(optionId: string): void { this.active.delete(optionId); }

  resultFor(assetId: string): string | undefined {
    for (const context of this.active.values()) {
      if (context.assetId === assetId) {
        const url = this.ready.get(context.key);
        if (url) return url;
      }
    }
    return undefined;
  }

  async run(context: AiPhotoContext, cacheOnly: boolean): Promise<void> {
    this.active.set(context.optionId, context);
    const known = this.ready.get(context.key);
    if (known) {
      this.ports.changed(context, { phase: 'ready', url: known }, cacheOnly);
      return;
    }
    this.ports.changed(context, { phase: 'working' }, cacheOnly);
    // A size ID remap while an authorized generation is running follows that
    // request. It cannot turn an unfinished cache read into a second paid call.
    let request = this.generating.get(context.key);
    if (!request) {
      request = Promise.resolve().then(() => this.ports.request(context, cacheOnly))
        .catch(() => ({ url: null, reason: 'network' }));
      if (!cacheOnly) this.generating.set(context.key, request);
    }
    const result = await request;
    if (this.generating.get(context.key) === request) this.generating.delete(context.key);
    if (result.url) this.ready.set(context.key, result.url);
    // Old photo, old words, or an old form must never repaint the current design.
    if (this.active.get(context.optionId) !== context) return;
    if (result.url) {
      this.ports.changed(context, { phase: 'ready', url: result.url }, cacheOnly);
    } else {
      this.ports.changed(context, {
        phase: 'missing',
        message: result.notice ?? 'Dizajn za ovaj izbor još nije spreman. Napravi dizajn pre dodavanja u korpu.',
      }, cacheOnly);
    }
  }
}
